/**
 * Lesson Store — Named Selectors
 *
 * Provides referentially stable selector functions for useLessonStore.
 * Import these instead of declaring inline `(s) => s.foo` selectors in screens
 * to avoid unnecessary re-renders and reduce duplication.
 *
 * NOTE: LessonState is not exported from lessonStore.ts, so we derive the type
 * via ReturnType<typeof useLessonStore.getState>.
 */

import { useLessonStore } from './lessonStore';

type LessonState = ReturnType<typeof useLessonStore.getState>;

// ── Lesson path ──────────────────────────────────────────────────────

export const selectLessonPath = (s: LessonState) => s.lessonPath;
export const selectSelectedLevel = (s: LessonState) => s.selectedLevel;
export const selectIsLoadingPath = (s: LessonState) => s.isLoadingPath;
export const selectPathError = (s: LessonState) => s.pathError;

// ── Recommendations ──────────────────────────────────────────────────

export const selectRecommendations = (s: LessonState) => s.recommendations;
export const selectIsLoadingRecommendations = (s: LessonState) => s.isLoadingRecommendations;
export const selectRecommendationsError = (s: LessonState) => s.recommendationsError;

// ── Active lesson ────────────────────────────────────────────────────

export const selectCurrentLesson = (s: LessonState) => s.currentLesson;
export const selectIsLoadingLesson = (s: LessonState) => s.isLoadingLesson;
export const selectExerciseProgress = (s: LessonState) => s.exerciseProgress;
export const selectAiGeneratedExercises = (s: LessonState) => s.aiGeneratedExercises;
export const selectIsGeneratingExercises = (s: LessonState) => s.isGeneratingExercises;

// ── Completion ───────────────────────────────────────────────────────

export const selectIsSubmittingCompletion = (s: LessonState) => s.isSubmittingCompletion;
export const selectLastCompletionResult = (s: LessonState) => s.lastCompletionResult;

// ── Due for review ───────────────────────────────────────────────────

export const selectDueLessons = (s: LessonState) => s.dueLessons;
export const selectDueTotal = (s: LessonState) => s.dueTotal;
export const selectDueLoaded = (s: LessonState) => s.dueLoaded;
export const selectDueLoading = (s: LessonState) => s.dueLoading;
export const selectDueError = (s: LessonState) => s.dueError;
export const selectLoadDueLessons = (s: LessonState) => s.loadDueLessons;

// ── Errors ───────────────────────────────────────────────────────────

export const selectLessonError = (s: LessonState) => s.lessonError;
export const selectExerciseError = (s: LessonState) => s.exerciseError;
export const selectExerciseErrorCode = (s: LessonState) => s.exerciseErrorCode;
export const selectCompletionError = (s: LessonState) => s.completionError;

// ── Lesson update flags ──────────────────────────────────────────────

export const selectLessonUpdatesAvailable = (s: LessonState) => s.lessonUpdatesAvailable;

// ── Actions: Path ────────────────────────────────────────────────────

export const selectFetchLessonPath = (s: LessonState) => s.fetchLessonPath;
export const selectFetchRecommendations = (s: LessonState) => s.fetchRecommendations;
export const selectSetSelectedLevel = (s: LessonState) => s.setSelectedLevel;

// ── Actions: Lesson lifecycle ────────────────────────────────────────

export const selectFetchLesson = (s: LessonState) => s.fetchLesson;
export const selectGenerateExercises = (s: LessonState) => s.generateExercises;
export const selectUpdateExerciseProgress = (s: LessonState) => s.updateExerciseProgress;
export const selectSubmitCompletion = (s: LessonState) => s.submitCompletion;
export const selectDismissCompletion = (s: LessonState) => s.dismissCompletion;
export const selectRetryLesson = (s: LessonState) => s.retryLesson;

// ── Actions: Cleanup ─────────────────────────────────────────────────

export const selectResetLessonState = (s: LessonState) => s.resetLessonState;
export const selectClearLessonErrors = (s: LessonState) => s.clearErrors;
export const selectClearExerciseError = (s: LessonState) => s.clearExerciseError;

// ── Actions: Lesson updates ──────────────────────────────────────────

export const selectSetLessonUpdatesAvailable = (s: LessonState) => s.setLessonUpdatesAvailable;
export const selectClearLessonUpdate = (s: LessonState) => s.clearLessonUpdate;
